/* src/App.js - v13.47 - Logout Revokes The Server Token
   CHANGELOG (v13.46 -> v13.47): handleLogout() now calls POST /api/logout first (the fetch wrapper in
   index.js adds the Bearer token) so the server revokes the login token, then clears local storage as
   before. Best effort: if the call fails or hangs the user is still logged out locally after 2 seconds.
   CHANGELOG (v13.45 -> v13.46): Added a mount-time console
   warning (Facebook's own wording/approach) that fires a big styled
   "Stop!" message the moment anyone opens DevTools, warning against
   pasting code from self-XSS scams. Pure console.log with CSS
   formatting - no behavior change, can't actually block DevTools from
   JS, this is user education only, same as Facebook's own version.
   CHANGELOG (v13.44 -> v13.45): Per request, matched the
   banner's exact visual styling to Music Underground's (measured its
   live computed styles: #dc2626 background - Tailwind's red-600, not
   the app's usual #ff4757 - 8px/16px padding, 14px/700 font) so the
   same alert looks consistent across both sites instead of NSN's own
   larger/differently-colored version.
   CHANGELOG (v13.43 -> v13.44): The outage banner
   (v13.42) was rendered as the first child INSIDE .app-container, which
   is `display:flex` (row, the main layout's left-rail/feed/widgets
   columns) with `min-height:100vh` - as a flex item with no explicit
   width, it shrank to content width and stretched to 100vh height
   (flex's default align-items:stretch), rendering as a narrow vertical
   bar down the left edge instead of a banner across the top. Confirmed
   visually - reported as "looks good on Music Underground [a plain
   sibling <div>, unaffected by this], but not on NSN." Moved outside
   .app-container entirely (a preceding sibling, wrapped in a Fragment)
   so it's laid out in normal document flow, unaffected by the app's
   internal flex layout.
   CHANGELOG (v13.42 -> v13.43): outage.py (kb-admin, the
   Nubix Cloud Admin Panel) has managed a shared outage alert
   (/var/www/status/outage_alert.json, served with CORS from
   kb.nubix.ai specifically so other sites can read it) since it was
   built - "other sites reading it (NSN, Music Underground)" was the
   whole point per its own file header - but NSN never actually fetched
   or displayed it. Toggling the alert on in kb-admin did nothing here.
   Added a fetch on mount + a site-wide red banner (same visual as
   kb.nubix.ai's own #kb-outage-banner) shown whenever it's active,
   regardless of which view is open. Fails silently (.catch(() => {}))
   if kb.nubix.ai is unreachable - a missing alert banner is much better
   than this fetch ever breaking the app itself.
   CHANGELOG (v13.41 -> v13.42): Profile.js/GroupDetails.js
   now fetch their own separate slice of posts (v6.31/v2.18) instead of
   sharing App.js's `posts` state, which meant liking/commenting/voting
   from those pages updated the global feed state but not the local list
   those pages actually render from - the action worked server-side but
   visibly appeared to do nothing, a real "it's broken" complaint waiting
   to happen for a reaction feature. Every mutation handler here now
   RETURNS its result (the updated post, or true/false for delete)
   instead of being a fire-and-forget void function, so any caller -
   Profile.js/GroupDetails.js's new wrapper functions - can apply the
   same update to its own local state too, not just App.js's.
   CHANGELOG (v13.40 -> v13.41): The live feed downloaded
   EVERY post in the database (1,532 and growing) on initial load, on a
   30-second poll, and after every single like/comment/vote/post/delete -
   confirmed root cause of a reported "freezes for several seconds on
   open" issue. fetchPosts() now loads one page (POSTS_PAGE_SIZE=30) via
   the newly-paginated /api/posts/archive; loadMorePosts() fetches
   further pages (Feed.js's new infinite-scroll sentinel calls it);
   checkForNewPosts() replaces the 30s full-refetch poll with a cheap
   /api/posts/new?after=<id> check that only downloads what's actually
   new. Every mutation handler (add/delete/like/comment/vote/edit) now
   patches `posts` locally from that mutation's own response instead of
   calling fetchPosts() afterward - all of them already returned the
   updated post, this just stopped throwing that away. Widgets and
   Profile/GroupDetails no longer receive the full `posts` array (it's
   only ever a partial page now) - they fetch their own data from the
   new /api/widgets/summary and the extended /api/posts/archive
   (username/group filters) respectively. Trending-tag search
   (homePosts' old client-side filter) and the single-post deep-link
   view (post_details) also switched to dedicated fetches for the same
   reason - both used to implicitly work only because `posts` held
   every post ever made.
   CHANGELOG (v13.39 -> v13.40): The welcome modal's body used to be three
   hardcoded-default plain-text fields (welcome_msg_1/2/3) with no admin
   UI to edit them at all. Replaced with welcome_message_html and
   whats_new_html (app.py v13.7), rendered via dangerouslySetInnerHTML -
   same established pattern as TermsOfService.js/PrivacyPolicy.js - now
   editable as rich HTML from the new Admin Dashboard "What's New Modal"
   tab (AdminDashboard.js v5.5). See App.css v2.20 for the new
   .welcome-modal-msg/.welcome-modal-whats-new wrapper styling (base
   color/link/list styles, since arbitrary admin HTML replaces the old
   per-<p> inline styles).
   CHANGELOG (v13.38 -> v13.39): Two fixes from live feedback on v13.38's
   new features.
   (1) .scroll-top-btn was anchored to the viewport's left edge (fixed
   left: 20px), which on a wide browser window sits far away from the
   actual (centered, max-width-capped) feed column. Now tracks the real
   feed-wrapper element's position via a ref + getBoundingClientRect,
   recalculated on mount/resize/view change, so the button always hugs
   the left edge of the actual feed regardless of window width.
   (2) Reported bug: a native browser "install this page as an app"
   banner was appearing, and was being mistaken for/confused with the new
   Play Store badge - it's actually Chrome's own PWA install prompt,
   triggered automatically by public/manifest.json's display:"standalone"
   config (pre-existing, unrelated to anything just built). That's
   wrong for this project - the real native Android app already exists
   separately, so installing the *website* as its own separate PWA icon
   is exactly the confusing behavior Kieran flagged. Fixed two ways:
   (a) intercept and suppress Chrome's native beforeinstallprompt so it
   never shows, (b) added a custom one-time top banner (mobile Android
   only, localStorage-gated so it only ever shows once per browser
   profile unless site data/cache is cleared) that offers the same
   open-app-or-Play-Store action as Login.js's permanent badge - a
   reminder, not a repeated nag.
   CHANGELOG (v13.37 -> v13.38): Reported bug: "jump to top" never worked
   properly on feed/profile/group navigation. Root cause: handleScrollTop
   set mainContentRef.current.scrollTop = 0, but .main-content has no
   overflow-y anywhere in App.css - it never scrolls internally, so that
   line was always a no-op. The real scrolling element is the window
   itself, and the only line doing real work (window.scrollTo(0,0)) was
   wrapped in a fixed 50ms setTimeout - a guess that raced against
   fetchPosts()/images still loading, flaky depending on network speed.
   Replaced with a double requestAnimationFrame, which reliably waits for
   the browser to actually paint the new view before scrolling, with no
   dependency on how long content takes to arrive. Also added a universal
   floating "back to top" button (.scroll-top-btn, App.css v2.17) that
   appears after scrolling 400px on any view except Messages (which has
   its own internal chat-scroll containers, not a window-scroll page) -
   covers feed, profile, groups, and group details in one place instead of
   per-page sticky headers.
   CHANGELOG (v13.36 -> v13.37): Reported bug: clicking a trending tag
   pulled in irrelevant posts. homePosts' searchTag filter was matching
   with a loose .toLowerCase().includes(searchTag) against the whole
   message body - "ai" matched any post containing that substring
   anywhere ("container", "maintain", "email", ...), not just posts
   actually tagged #ai. Now extracts real hashtags from the message
   (same /#\w+/g pattern Widgets.js already uses to build the trending
   list) and checks for an exact match instead. See also Widgets.js
   v5.9's separate fix for why the trending list itself never seemed to
   change.
   CHANGELOG (v13.35 -> v13.36): Comment Up/Down Voting
   CHANGELOG (v13.35 -> v13.36): Added handleVoteComment (PUT
   /api/posts/<pid>/comment/<cid>/vote, routes/posts.py v2.9), passed as
   onVoteComment to every place onAddComment already goes (both direct
   <Feed> renders, GroupDetails, Profile - GroupDetails/Profile forward it
   into their own internal <Feed> the same way they already forward
   onAddComment). Follows the same fire-and-refetch pattern already used
   by delete/edit comment, not the optimistic local-state pattern post
   likes use - keeps this consistent with how comment actions already
   behave rather than introducing a second pattern.
   CHANGELOG (v13.34 -> v13.35): handleAddComment now accepts and forwards
   an optional image (routes/posts.py v2.8's new comment field) - part of
   the new comment attachments/GIF/emoji feature, see Post.js and Feed.js
   for where it's actually captured/set.
   CHANGELOG (v13.33 -> v13.34): Profile.js was only ever given
   onNavigate={setView} (a bare view-name setter) - it never received a
   real "go to this OTHER user's profile" callback. Its own
   handleProfileNavigation(username) called onNavigate(username), which
   under the hood was setView(username) - setting the view state to a
   literal username string that matches none of App.js's view === '...'
   render branches, so NOTHING rendered (a blank page). This was dead
   code until the new Friends-list feature became its first real caller
   with a genuinely different target user - clicking your own name (the
   only prior code path) always hit handleProfileNavigation's early
   return and never actually reached onNavigate at all, so the bug was
   never exercised before. Added onNavigateProfile={handleNavigateProfile}
   (the real function - fetches the target user, sets viewedUser AND
   view together) as a new prop; Profile.js now calls that instead.
   CHANGELOG (v13.32 -> v13.33): Widened the notifications dropdown from
   300px to 340px, per explicit request while bringing mobile's
   NotificationsPanel.js up to visual parity with this same panel.
   CHANGELOG (v13.31 -> v13.32): Suspending an account via the admin
   dashboard previously only blocked the *next* login attempt - anyone
   already logged in kept full access until they happened to log out.
   checkUnread() already polls /api/messages/unread-count every 5s; that
   response now also carries 'suspended' (routes/messages.py v2.1), so no
   new endpoint or timer was needed. If it comes back true, silently clear
   storage and log out - no in-app message, per explicit decision (the
   suspension notification email/in-app alert already sent at suspend time
   covers that).
   CHANGELOG (v13.30 -> v13.31):
   - Every /api/user/public/ fetch now sends ?requester=... - none of them
     did before, which meant the backend's 'friends'/is_self checks always
     saw requester_name as empty, so a 'friends'-only profile 403'd for
     literally everyone (including actual mutual friends and even a self-
     refresh of your own profile if you'd set your own privacy to
     'friends'). Confirmed via live testing while building the new friend-
     request flow, not something introduced by it.
   - handleNavigateProfile now actually handles the non-ok (403) case -
     previously it did NOTHING at all on a blocked profile (view never
     even changed), leaving the user looking at whatever screen they were
     already on with no feedback. Now sets a minimal __gated viewedUser
     shape that Profile.js v6.23 renders as a 🔒 + working Follow/Requested
     button.
   - Added handleRespondToFriendRequest + inline Accept/Decline buttons in
     the notifications dropdown for type:'friend_request' notifications -
     these no longer just click-to-navigate like other notification types.
   See routes/users.py v2.3/v2.4 for the backend side: profile_privacy
   'friends' targets now require the owner's approval before a follow
   connects at all, and accepting makes the relationship mutual.
   CHANGELOG (v13.29 -> v13.30): handleNotificationClick now checks
   n.target.group_slug before the generic 'post' branch - a group_post
   notification's target has kind:'post' (it still carries a real post_id)
   but ALSO group_slug, and was incorrectly falling into the plain post
   branch, opening a generic single-post view instead of the actual group.
   Same fix applied to mobile's NotificationsPanel.js, same release.
   CHANGELOG (v13.28 -> v13.29): app.py's global_search() (v14.0) replaced
   its old hardcoded per-category caps (20/10/5/5/10, unsorted) with
   alphabetically-sorted (posts by id) results, offset-based pagination,
   and a *_has_more flag per category - bare '@' used to silently drop 9
   of 29 real users with zero way to reach them. Added
   globalSearchHasMore/loadingMoreCategory state and handleLoadMoreSearch,
   which re-fetches with that category's current result count as its
   offset (correct regardless of the backend's actual page size) and
   appends rather than replaces. Render block now shows a "Load More"
   button per section instead of silently truncating.
   CHANGELOG (v13.27 -> v13.28):
   - The Timeline toggle button lived inside Feed.js, in its own row above
     the post composer - it was easy to miss and left an odd gap. Moved it
     into the Newsfeed header bar here, next to the notification bell and
     search icon, matching that row's existing style. showTimeline state
     lifted up from Feed.js so this header icon can control it directly.
   CHANGELOG (v13.26 -> v13.27):
   - Added eslint-disable-next-line for react-hooks/exhaustive-deps on the
     URL-push effect (missing dependency: getPathForState). Excluding it is
     intentional - it's a plain function recreated every render, so listing
     it would make the effect fire on every render instead of only when the
     view-related state actually changes.
   CHANGELOG (v13.25 -> v13.26):
   - The address bar now updates to reflect in-app navigation (profile,
     groups, group details, messages/AI chat, post details, terms, privacy),
     using history.pushState. Previously the URL never changed after the
     initial page load, since the app had no client-side routing at all.
   - Browser Back/Forward buttons now work correctly via a popstate
     listener, which re-parses the target URL the same way the initial
     deep-link load does.
   - The deep-link parsing logic from v13.24/v13.25 was refactored into a
     single reusable parser (applyPathToStateRef) shared by initial load and
     Back/Forward navigation, instead of being duplicated.
   - Known minor limitation: clicking the general "Messages" sidebar icon
     does not clear a previously-selected conversation (chatTarget), so the
     URL may still show /messages/<last-viewed-user> immediately afterward
     rather than a bare /messages. This matches pre-existing internal state
     behavior in Messages.js and wasn't introduced by this change.
   CHANGELOG (v13.24 -> v13.25):
   - Added eslint-disable-next-line for react-hooks/exhaustive-deps on the
     deep-link parsing effect. The empty dependency array is intentional
     (the effect must run exactly once, on initial mount, to read the URL) -
     this just documents that intent so the build no longer warns about it.
   CHANGELOG (v13.23 -> v13.24):
   - Path parsing on load now handles /profile/<name>, /groups/<slug>, and
     /messages/<name> in addition to the existing /post/<id> support.
   - /messages/Nexus opens the Ask AI tab specifically; any other
     /messages/<name> opens the Chats tab with that conversation selected.
   - /messages/* requires login (matches in-app behavior): if a deep link to
     a conversation is opened while logged out, the login modal opens and the
     intended conversation is resumed automatically right after login,
     instead of being lost.
   - /profile/<name> and /groups/<slug> work for guests too, matching how
     those views already behave when navigated to in-app. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

import AdminDashboard from './components/AdminDashboard';
import Sidebar from './components/Sidebar';
import Feed from './components/Feed';
import Widgets from './components/Widgets';
import Profile from './components/Profile';
import Groups from './components/Groups';      
import Messages from './components/Messages'; 
import Login from './components/Login'; 
import ResetPassword from './components/ResetPassword';
import NotificationPrefs from './components/NotificationPrefs'; 
import GroupDetails from './components/GroupDetails'; 
import DraggableFeedback from './components/DraggableFeedback';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';

import MENU_ITEMS from './menuConfig.json';

const API_URL = '/api/posts';
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//uQxAAAAANIAAAAAExBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq';

const wallpapers = [ 
  "/backgrounds/background1.jpg", "/backgrounds/background2.jpg", "/backgrounds/background3.jpg", 
  "/backgrounds/background4.jpg", "/backgrounds/background5.jpg", "/backgrounds/background6.jpg", 
  "/backgrounds/background7.jpg", "/backgrounds/background8.jpg", "/backgrounds/background9.jpg", 
  "/backgrounds/background10.jpg", "/backgrounds/background11.jpg", "/backgrounds/background12.jpg", 
  "/backgrounds/background13.jpg" 
];

const getSafeImage = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return path;
    return `/${path}`;
};

const GUEST_USER_OBJ = {
    name: 'Guest',
    username: 'guest',
    avatarColor: '#555',
    profile_pic: null,
    isGuest: true
};

const PageTitle = ({ title }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
};

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('nsn_user');
    if (savedUser) {
        const u = JSON.parse(savedUser);
        if (!u.name && u.username) u.name = u.username;
        return u;
    }
    return null;
  });

  // Loads the AdSense script at runtime rather than baking it into
  // index.html's static <head> - this SPA serves the SAME index.html for
  // every route (nginx's try_files fallback), so a static script tag put
  // the ad script on EVERY "screen" including the two bare, no-real-
  // content utility pages (ResetPassword.js/NotificationPrefs.js) that
  // only ever show a one-line status message with no token. AdSense's
  // review flagged exactly this: "Google-served ads on screens without
  // publisher-content... used for alerts, navigation or other behavioral
  // purposes" - a spot-on description of those two pages' bare/expired-
  // token state. Skipping script injection there entirely so Google's
  // crawler never finds an ad-eligible "screen" at those URLs.
  useEffect(() => {
    const AD_EXCLUDED_PATHS = ['/reset-password', '/notifications'];
    if (AD_EXCLUDED_PATHS.includes(window.location.pathname)) return;
    if (document.querySelector('script[src*="adsbygoogle.js"]')) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3595170434836466';
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  }, []);

  const [showLoginModal, setShowLoginModal] = useState(false); 
  const [showTimeline, setShowTimeline] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [dontShowWelcomeAgain, setDontShowWelcomeAgain] = useState(false);
  const [welcomeConfig, setWelcomeConfig] = useState(null); 

  const [view, setView] = useState(() => localStorage.getItem('nsn_view') || 'home');
  const [currentGroupSlug, setCurrentGroupSlug] = useState(() => localStorage.getItem('nsn_group_slug') || null);
  
  const [currentPostId, setCurrentPostId] = useState(null);
  const [viewedUser, setViewedUser] = useState(null); 
  const [searchTag, setSearchTag] = useState(null); 
  const [profileParams, setProfileParams] = useState(null); 
  const [messagesTab, setMessagesTab] = useState('chats');
  const [feedbackPrompt, setFeedbackPrompt] = useState(null); 
  const [chatTarget, setChatTarget] = useState(null);

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState({ users: [], posts: [], groups: [] });
  const [globalSearchHasMore, setGlobalSearchHasMore] = useState({ users: false, posts: false, groups: false });
  const [isSearching, setIsSearching] = useState(false);
  const [loadingMoreCategory, setLoadingMoreCategory] = useState(null); // null | 'users' | 'groups' | 'posts'
  
  const [notifications, setNotifications] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [aiUnread, setAiUnread] = useState(0); 
  
  const audioRef = useRef(new Audio(NOTIFICATION_SOUND));
  const lastUnreadCountRef = useRef(0); 
  const mainContentRef = useRef(null);
  const [showBottomNav, setShowBottomNav] = useState(true);
  const touchStartRef = useRef(null);
  const [showScrollTopBtn, setShowScrollTopBtn] = useState(false);
  const feedWrapperRef = useRef(null);
  const [scrollBtnLeft, setScrollBtnLeft] = useState(20);
  const [showAppPrompt, setShowAppPrompt] = useState(false);

  // --- Self-XSS console warning (v13.46) ---
  useEffect(() => {
      console.log(
          '%cStop!',
          'color: #ff4757; font-size: 60px; font-weight: bold; text-shadow: 1px 1px 0 #000;'
      );
      console.log(
          '%cThis is a browser feature intended for developers. If someone told you to copy and paste something here to enable an NSN feature or "hack" someone\'s account, it is a scam and will give them access to your account.',
          'font-size: 16px;'
      );
  }, []);

  // --- HOTFIX: FORCE MOBILE DARK MODE ---
  useEffect(() => {
      let metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (!metaThemeColor) {
          metaThemeColor = document.createElement("meta");
          metaThemeColor.name = "theme-color";
          document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.content = "#1e1e24"; 
      document.body.style.backgroundColor = "#1e1e24";
  }, []);

  useEffect(() => {
      fetch('/api/config/public')
        .then(res => res.json())
        .then(data => {
            setWelcomeConfig(data);
            const hasSeenWelcome = localStorage.getItem('nsn_welcome_seen');
            if (!user && !hasSeenWelcome && data.welcome_title) {
                setTimeout(() => setShowWelcomeModal(true), 500);
            }
        })
        .catch(err => console.error("Config fetch error:", err));
  }, [user]);

  const handleCloseWelcome = () => {
      if (dontShowWelcomeAgain) {
          localStorage.setItem('nsn_welcome_seen', 'true');
      }
      setShowWelcomeModal(false);
  };

  useEffect(() => {
      localStorage.setItem('nsn_view', view);
      if (currentGroupSlug) localStorage.setItem('nsn_group_slug', currentGroupSlug);
      else localStorage.removeItem('nsn_group_slug');
  }, [view, currentGroupSlug]);

  // --- URL SYNC: central path parser (used on load AND on browser Back/Forward) ---
  // Stored in a ref (reassigned every render) so the mount-only popstate
  // listener below can always call the freshest version - capturing current
  // `user` and handler functions - without needing to re-register the
  // listener or list them all as effect dependencies.
  const applyPathToStateRef = useRef();
  applyPathToStateRef.current = (path) => {
      if (path.startsWith('/post/')) {
          const pid = parseInt(path.split('/')[2]);
          if (pid) { setCurrentPostId(pid); setView('post_details'); }
          return;
      }

      if (path.startsWith('/profile/')) {
          const targetName = decodeURIComponent(path.split('/')[2] || '');
          if (targetName) handleNavigateProfile(targetName);
          return;
      }

      if (path.startsWith('/groups/')) {
          const slug = path.split('/')[2];
          if (slug) handleNavigateGroup(slug);
          else setView('groups');
          return;
      }
      if (path === '/groups') { setView('groups'); return; }

      if (path.startsWith('/messages')) {
          const targetName = decodeURIComponent(path.split('/')[2] || '');

          if (!targetName) {
              if (!user) { setShowLoginModal(true); return; }
              setMessagesTab('chats');
              setView('messages');
              return;
          }

          // Messaging (including Nexus/Ask AI) requires an account, matching
          // in-app behavior. If logged out, remember the destination and
          // prompt login; handleLogin() resumes it after a successful login.
          if (!user) {
              setPendingDeepLink({ type: 'messages', target: targetName });
              setShowLoginModal(true);
              return;
          }

          if (targetName.toLowerCase() === 'nexus') {
              handleStartAIChat();
          } else {
              handleStartMessage(targetName);
          }
          return;
      }

      if (path === '/terms') { setView('terms'); return; }
      if (path === '/privacy') { setView('privacy'); return; }
      if (path === '/' || path === '') { setView('home'); return; }
  };

  // Tracks whether a state change came from the user hitting Back/Forward
  // (browser already updated the URL - don't push a new entry) vs. an
  // in-app navigation (we need to push the URL ourselves).
  const isPopStateNav = useRef(false);
  // Skips the very first run of the URL-push effect below (the state at
  // mount already matches the URL it came from - nothing to push yet).
  const hasMountedPushEffect = useRef(false);

  // Read the URL once on initial load (handles deep links + shared links).
  useEffect(() => {
      applyPathToStateRef.current(window.location.pathname);
  }, []);

  // Handle browser Back/Forward buttons the same way as initial load: just
  // re-parse whatever path the browser has navigated to.
  useEffect(() => {
      const handlePopState = () => {
          isPopStateNav.current = true;
          applyPathToStateRef.current(window.location.pathname);
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Compute the canonical URL path for the current view-related state.
  const getPathForState = () => {
      if (view === 'profile') {
          const name = viewedUser ? viewedUser.name : (user ? user.name : null);
          return name ? `/profile/${encodeURIComponent(name)}` : '/';
      }
      if (view === 'group_details' && currentGroupSlug) return `/groups/${currentGroupSlug}`;
      if (view === 'groups') return '/groups';
      if (view === 'post_details' && currentPostId) return `/post/${currentPostId}`;
      if (view === 'messages') {
          if (messagesTab === 'ai') return '/messages/Nexus';
          if (chatTarget) return `/messages/${encodeURIComponent(chatTarget)}`;
          return '/messages';
      }
      if (view === 'terms') return '/terms';
      if (view === 'privacy') return '/privacy';
      return '/';
  };

  // Keep the address bar in sync whenever in-app navigation changes the view.
  useEffect(() => {
      if (!hasMountedPushEffect.current) {
          hasMountedPushEffect.current = true;
          return; // initial state already matches the URL it came from
      }
      if (isPopStateNav.current) {
          isPopStateNav.current = false;
          return; // Back/Forward - browser already updated the URL
      }
      const newPath = getPathForState();
      if (newPath !== window.location.pathname) {
          window.history.pushState(null, '', newPath);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getPathForState is intentionally excluded: it's a plain function recreated every render, so listing it would make this effect fire on every render instead of only when the listed state actually changes
  }, [view, currentGroupSlug, currentPostId, viewedUser, messagesTab, chatTarget]);

  useEffect(() => {
      if (user) {
          const lookupName = user.name || user.username;
          fetch(`/api/user/public/${lookupName}?requester=${encodeURIComponent(lookupName)}`).then(res => res.json()).then(data => {
                if (data && (data.name || data.username)) {
                    const freshUser = { ...user, ...data };
                    if (!freshUser.name && freshUser.username) freshUser.name = freshUser.username;
                    if (JSON.stringify(freshUser) !== JSON.stringify(user)) {
                        setUser(freshUser);
                        localStorage.setItem('nsn_user', JSON.stringify(freshUser));
                    }
                }
            }).catch(e => console.error("User refresh failed", e));
      }
  }, [user]);

  useEffect(() => {
      const delayDebounceFn = setTimeout(async () => {
        if (!globalSearchTerm.trim()) {
            setGlobalSearchResults({ users: [], posts: [], groups: [] });
            setGlobalSearchHasMore({ users: false, posts: false, groups: false });
            return;
        }
        setIsSearching(true);
        try {
            const res = await fetch(`/api/search?q=${encodeURIComponent(globalSearchTerm)}`);
            if (res.ok) {
                const data = await res.json();
                setGlobalSearchResults({
                    users: data.users || [],
                    posts: data.posts || [],
                    groups: data.groups || []
                });
                setGlobalSearchHasMore({
                    users: !!data.users_has_more,
                    posts: !!data.posts_has_more,
                    groups: !!data.groups_has_more
                });
            }
        } catch (e) { console.error("Search failed", e); } finally { setIsSearching(false); }
      }, 500);
      return () => clearTimeout(delayDebounceFn);
  }, [globalSearchTerm]);

  // Re-fetches with this category's current result count as its offset
  // (correct regardless of the backend's actual page size) and appends
  // rather than replacing - see v13.29 changelog above.
  const handleLoadMoreSearch = async (category) => {
      setLoadingMoreCategory(category);
      try {
          const offsetParam = `${category}_offset`;
          const res = await fetch(`/api/search?q=${encodeURIComponent(globalSearchTerm)}&${offsetParam}=${globalSearchResults[category].length}`);
          if (res.ok) {
              const data = await res.json();
              setGlobalSearchResults(prev => ({ ...prev, [category]: [...prev[category], ...(data[category] || [])] }));
              setGlobalSearchHasMore(prev => ({ ...prev, [category]: !!data[`${category}_has_more`] }));
          }
      } catch (e) { console.error("Load more search failed", e); } finally { setLoadingMoreCategory(null); }
  };

  const [introPromptText, setIntroPromptText] = useState(null);

  const handleLogin = (userData) => {
    if (userData.profile_pic) userData.profile_pic = getSafeImage(userData.profile_pic);
    if (!userData.name && userData.username) userData.name = userData.username;
    setUser(userData);
    setShowLoginModal(false); 
    localStorage.setItem('nsn_user', JSON.stringify(userData));

    // First real login right after registering (see Login.js) - open the
    // composer pre-filled with an intro-post starter instead of an empty
    // feed. Read-once: cleared immediately so it never reappears on a
    // later login or on onUpdateUser (this same handler doubles as
    // Profile.js's post-edit refresh callback).
    if (sessionStorage.getItem('nsn_just_registered')) {
        sessionStorage.removeItem('nsn_just_registered');
        const name = userData.name || userData.username;
        setIntroPromptText(`\u{1F44B} Hi everyone, I'm ${name}! Just joined NSN - `);
    }

    // Resume a deep-linked destination (e.g. /messages/<name>) that required
    // login before it could be opened.
    if (pendingDeepLink) {
        if (pendingDeepLink.type === 'messages') {
            if (pendingDeepLink.target.toLowerCase() === 'nexus') {
                handleStartAIChat();
            } else {
                handleStartMessage(pendingDeepLink.target);
            }
        }
        setPendingDeepLink(null);
    }
  };

  const handleLogout = () => {
      let finished = false;
      const finish = () => { if (finished) return; finished = true; localStorage.clear(); window.location.href = '/'; };
      setTimeout(finish, 2000);   // never leave the user stuck if the server is slow
      fetch('/api/logout', { method: 'POST' }).then(finish, finish);
  };

  const [backgroundStyle, setBackgroundStyle] = useState({
      backgroundColor: '#1e1e24',
      backgroundImage: `linear-gradient(rgba(13, 13, 18, 0.75), rgba(13, 13, 18, 0.85))`
  });

  const [outageAlert, setOutageAlert] = useState(null);
  useEffect(() => {
      fetch('https://kb.nubix.ai/status/outage_alert.json')
          .then(res => res.ok ? res.json() : null)
          .then(data => { if (data && data.active) setOutageAlert(data); })
          .catch(() => {});
  }, []);
  
  const [posts, setPosts] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [viewedPost, setViewedPost] = useState(null);
  const POSTS_PAGE_SIZE = 30;

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: POSTS_PAGE_SIZE, t: Date.now() });
      if (user) params.set('current_user', user.name);
      const response = await fetch(`/api/posts/archive?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(Array.isArray(data.posts) ? data.posts : []);
        setHasMorePosts(!!data.has_more);
      }
    } catch (error) { console.error(error); setPosts([]); }
  }, [user]);

  const loadMorePosts = useCallback(async () => {
    if (loadingMorePosts || !hasMorePosts || posts.length === 0) return;
    setLoadingMorePosts(true);
    try {
      const oldest = posts[posts.length - 1]?.id;
      const params = new URLSearchParams({ limit: POSTS_PAGE_SIZE, before: oldest });
      if (user) params.set('current_user', user.name);
      const res = await fetch(`/api/posts/archive?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => [...prev, ...(data.posts || [])]);
        setHasMorePosts(!!data.has_more);
      }
    } catch (error) { console.error(error); }
    setLoadingMorePosts(false);
  }, [posts, hasMorePosts, loadingMorePosts, user]);

  // Lightweight background catch-up - replaces the old "refetch
  // everything every 30s" poll. Only downloads posts newer than what's
  // already loaded; if too much happened to catch up incrementally
  // (truncated), falls back to a normal paginated reload instead of
  // silently showing a gappy feed.
  const checkForNewPosts = useCallback(async () => {
    if (posts.length === 0) return;
    try {
      const newest = posts[0]?.id;
      const params = new URLSearchParams({ after: newest });
      if (user) params.set('current_user', user.name);
      const res = await fetch(`/api/posts/new?${params}`);
      if (res.ok) {
        const data = await res.json();
        if (data.truncated) {
          fetchPosts();
        } else if (data.posts && data.posts.length > 0) {
          setPosts(prev => [...data.posts, ...prev]);
        }
      }
    } catch (error) { console.error(error); }
  }, [posts, user, fetchPosts]);

  // Applies a mutation's response (the API always returns the updated
  // post) to whichever of `posts`/`viewedPost` currently hold that post,
  // instead of the old approach of throwing the response away and
  // refetching the entire feed.
  const applyPostUpdate = useCallback((updatedPost) => {
    if (!updatedPost || !updatedPost.id) return;
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
    setViewedPost(prev => (prev && prev.id === updatedPost.id) ? updatedPost : prev);
  }, []);

  const applyPostDelete = useCallback((id) => {
    setPosts(prev => prev.filter(p => p.id !== id));
    setViewedPost(prev => (prev && prev.id === id) ? null : prev);
  }, []);

  useEffect(() => {
      fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
      const interval = setInterval(checkForNewPosts, 30000);
      return () => clearInterval(interval);
  }, [checkForNewPosts]);

  // Single-post deep link (notifications, shared links, search results) -
  // uses the copy already in `posts` if that page happens to include it
  // (keeps it in sync with feed mutations automatically), otherwise
  // fetches it directly rather than assuming it's in whatever page is
  // currently loaded.
  useEffect(() => {
      if (view !== 'post_details' || !currentPostId) { setViewedPost(null); return; }
      const inFeed = posts.find(p => p.id === currentPostId);
      if (inFeed) { setViewedPost(inFeed); return; }
      const params = new URLSearchParams();
      if (user) params.set('current_user', user.name);
      fetch(`/api/posts/${currentPostId}?${params}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => setViewedPost(data))
          .catch(err => { console.error(err); setViewedPost(null); });
  }, [view, currentPostId, posts, user]);

  // Trending-tag search (Widgets.js's 7-day trending list) - a dedicated
  // fetch rather than filtering `posts` client-side, since a clicked tag
  // needs to search beyond whatever page of the feed happens to be
  // loaded. null means "not searching" (show the normal feed).
  const [tagSearchResults, setTagSearchResults] = useState(null);
  useEffect(() => {
      if (!searchTag) { setTagSearchResults(null); return; }
      const params = new URLSearchParams({ tag: searchTag, limit: 50 });
      if (user) params.set('current_user', user.name);
      fetch(`/api/posts/archive?${params}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => setTagSearchResults(data ? data.posts : []))
          .catch(err => { console.error(err); setTagSearchResults([]); });
  }, [searchTag, user]);

  useEffect(() => {
    const randomImage = wallpapers[Math.floor(Math.random() * wallpapers.length)];
    setBackgroundStyle({ 
        backgroundColor: '#1e1e24',
        backgroundImage: `linear-gradient(rgba(13, 13, 18, 0.75), rgba(13, 13, 18, 0.85)), url(${randomImage})`, 
        backgroundSize: 'cover', 
        backgroundPosition: 'center' 
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const fetchNotifications = async () => {
        try {
            const res = await fetch(`/api/notifications?user=${user.name}`);
            if (res.ok) {
                const data = await res.json();
                setNotifications(data);
            }
        } catch (e) {}
    };
    
    const checkUnread = async () => {
        try {
            const res = await fetch(`/api/messages/unread-count?user=${user.name}`);
            const data = await res.json();

            if (data.suspended) {
                handleLogout();
                return;
            }

            const regularCount = data.count || 0;
            const nexusCount = data.ai_count || 0;
            const combined = regularCount + nexusCount;

            setTotalUnread(regularCount);
            setAiUnread(nexusCount);

            if (combined > lastUnreadCountRef.current) {
                try { audioRef.current.play().catch(e => {}); } catch(e){}
            }
            lastUnreadCountRef.current = combined;
        } catch (e) { }
    };

    fetchNotifications();
    checkUnread();
    
    const interval = setInterval(() => {
        fetchNotifications();
        checkUnread();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [user]);

  // FIXED (v13.38): see file header. Two stacked bugs, the second only
  // found via live testing (not visible from reading the CSS alone):
  // (1) the old 50ms setTimeout was a guess racing against async content
  // load - replaced with double rAF, which waits for the actual paint.
  // (2) window.scrollTo was targeting the wrong element - App.css's
  // `html, body { height: 100% }` (both with overflow set) makes body its
  // own independently-scrolling box here, separate from the window/
  // viewport scroll. Confirmed live: window.scrollTo(0,800) did nothing,
  // but document.body.scrollTop actually moved. Resetting all three
  // (window, documentElement, body) is the standard defensive fix for
  // this exact ambiguity - correct regardless of which one is the real
  // scrolling element.
  const handleScrollTop = () => {
      requestAnimationFrame(() => {
          requestAnimationFrame(() => {
              window.scrollTo(0, 0);
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
          });
      });
  };

  // Smooth animated version for the user-facing floating button click
  // (handleScrollTop above is instant, meant for right after a view change).
  const handleScrollToTopClick = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
      // Scroll events don't bubble, so a plain window listener misses
      // scrolling that's actually happening on body (see handleScrollTop's
      // comment above) - capture-phase on document catches both.
      const getScrollY = () => Math.max(
          window.scrollY || 0,
          document.documentElement.scrollTop || 0,
          document.body.scrollTop || 0
      );
      const handleAnyScroll = () => setShowScrollTopBtn(getScrollY() > 400);
      window.addEventListener('scroll', handleAnyScroll, { passive: true });
      document.addEventListener('scroll', handleAnyScroll, { passive: true, capture: true });
      return () => {
          window.removeEventListener('scroll', handleAnyScroll);
          document.removeEventListener('scroll', handleAnyScroll, { capture: true });
      };
  }, []);

  // Keeps .scroll-top-btn anchored to the actual feed column's left edge
  // instead of the viewport's - on a wide window the centered, max-width
  // feed sits far from the screen edge, so a plain fixed-left position
  // ended up nowhere near it. Recomputed on resize and on view change
  // (sidebar/layout can shift between views).
  useEffect(() => {
      const updateScrollBtnPos = () => {
          if (feedWrapperRef.current) {
              const rect = feedWrapperRef.current.getBoundingClientRect();
              setScrollBtnLeft(Math.max(20, rect.left - 60));
          }
      };
      updateScrollBtnPos();
      window.addEventListener('resize', updateScrollBtnPos);
      return () => window.removeEventListener('resize', updateScrollBtnPos);
  }, [view]);

  // Suppresses Chrome's native "install this page as an app" banner -
  // public/manifest.json's display:"standalone" makes the site itself
  // PWA-installable, which is wrong here since the real native Android
  // app already exists separately (see file header changelog).
  useEffect(() => {
      const handleBeforeInstallPrompt = (e) => { e.preventDefault(); };
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // One-time custom replacement for the suppressed native prompt above -
  // Android mobile only, shown once per browser profile (localStorage-
  // gated, so it naturally reappears if site data/cache is ever cleared,
  // matching the "permanent badge, one-time reminder" split Kieran asked
  // for). Same open-app-or-Play-Store link as Login.js's badge.
  useEffect(() => {
      const isAndroid = /Android/i.test(navigator.userAgent);
      if (isAndroid && !localStorage.getItem('nsn_app_prompt_seen')) {
          setTimeout(() => setShowAppPrompt(true), 1500);
      }
  }, []);

  const dismissAppPrompt = () => {
      localStorage.setItem('nsn_app_prompt_seen', 'true');
      setShowAppPrompt(false);
  };

  const handleOpenAppFromPrompt = () => {
      localStorage.setItem('nsn_app_prompt_seen', 'true');
      setShowAppPrompt(false);
      const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.nubix.social';
      window.location.href = `intent://#Intent;scheme=nubixsocial;package=com.nubix.social;S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`;
  };

  const handleMenuTouchStart = (e) => { touchStartRef.current = e.targetTouches[0].clientY; };
  const handleMenuTouchMove = (e) => {
      if (!touchStartRef.current) return;
      if (e.targetTouches[0].clientY - touchStartRef.current > 10) { setShowBottomNav(false); touchStartRef.current = null; }
  };

  const handleHardRefresh = () => { window.location.reload(); };

  const handleGoHome = () => { setSearchTag(null); fetchPosts(); setView('home'); handleScrollTop(); };
  const handleSearchTag = (tag) => { setSearchTag(tag); setView('home'); handleScrollTop(); };
  const handleNavigatePost = (postId) => { setCurrentPostId(postId); setView('post_details'); handleScrollTop(); };
  const handleNavigateGroup = (slug) => { setCurrentGroupSlug(slug); setView('group_details'); handleScrollTop(); };
  
  const handleNavigateProfile = async (targetName) => {
      setProfileParams(null); setShowSearchModal(false);
      if (user && targetName === user.name) { setViewedUser(null); setView('profile'); }
      else {
          const res = await fetch(`/api/user/public/${targetName}?requester=${encodeURIComponent(user?.name || '')}`);
          const data = await res.json();
          if (res.ok) {
              if (!data.name && data.username) data.name = data.username;
              setViewedUser(data);
          } else {
              // Previously this branch didn't exist at all - a blocked
              // (403) profile just silently did nothing (view never even
              // changed to 'profile'). Sets a minimal gated shape Profile.js
              // knows how to render (🔒 + a working Follow/Requested button)
              // instead of the full profile data the backend correctly
              // refuses to send.
              setViewedUser({ __gated: true, username: targetName, name: targetName, is_private: data.is_private, request_pending: data.request_pending, error: data.error });
          }
          setView('profile');
      }
      handleScrollTop();
  };

  const handleNavigateGallery = async (targetName, photoId) => {
      if (user && targetName !== user.name) {
          const res = await fetch(`/api/user/public/${targetName}?requester=${encodeURIComponent(user.name)}`);
          const data = await res.json();
          if (res.ok) { if (!data.name && data.username) data.name = data.username; setViewedUser(data); setView('profile'); }
      } else if (!user) {
          const res = await fetch(`/api/user/public/${targetName}`);
          const data = await res.json();
          if (res.ok) { if (!data.name && data.username) data.name = data.username; setViewedUser(data); }
      } else { setViewedUser(null); }
      setProfileParams({ tab: 'gallery', photoId: photoId }); setView('profile'); handleScrollTop();
  };

  const handleStartAIChat = () => {
      setMessagesTab('ai');
      setView('messages');
      handleScrollTop();
  };

  const handleStartMessage = (targetUsername) => {
      setChatTarget(targetUsername);
      setMessagesTab('chats');
      setView('messages');
      handleScrollTop();
  };

  const handleSidebarNavigate = (viewName) => {
      if (!user && ['messages', 'profile', 'nexus'].includes(viewName)) {
          setShowLoginModal(true);
          return;
      }
      if (viewName === 'nexus') { setMessagesTab('ai'); setView('messages'); } 
      else { 
          if (viewName === 'messages') setMessagesTab('chats'); 
          if (viewName === 'profile') handleNavigateProfile(user.name); 
          else setView(viewName); 
      }
      handleScrollTop();
  };

  const handleFeedbackClick = () => { 
      if(!user) return alert("Please login to send feedback.");
      setMessagesTab('ai'); 
      setView('messages');  
      window.scrollTo(0,0); 
  };

  const handleLegalNavigate = (targetView) => {
      setView(targetView);
      window.scrollTo(0,0); 
  };

  const handleClearNotifications = async () => {
      await fetch('/api/notifications/clear-all', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ username: user.name })
      });
      setNotifications([]);
      setShowNotifModal(false);
  };

  // Marks read one at a time on tap (the mark-read endpoint existed but
  // was never actually called anywhere before this - notifications only
  // ever got wiped wholesale via Clear All). Navigates using the target
  // field added to create_notification() (utils.py) - previously
  // notifications had no structured reference to what they were about, so
  // "tap to go to the activity" wasn't actually possible without
  // regex-parsing the text. Not every notification has a target (system
  // messages like admin feedback/report resolutions don't have one
  // relevant destination) - those just get marked read with no navigation.
  const handleNotificationClick = async (n) => {
      if (!n.read) {
          try {
              await fetch('/api/notifications/mark-read', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: n.id })
              });
              setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
          } catch (e) { console.error("Mark read failed", e); }
      }
      setShowNotifModal(false);
      if (!n.target) return;
      // A group_post notification's target has kind:'post' (see
      // routes/posts.py's notify_post_created) but ALSO carries
      // group_slug - check that FIRST, otherwise these fell into the
      // plain 'post' branch below and opened a generic single-post view
      // instead of the actual group the post was made in.
      if (n.target.group_slug) handleNavigateGroup(n.target.group_slug);
      else if (n.target.kind === 'post') handleNavigatePost(n.target.post_id);
      else if (n.target.kind === 'profile') handleNavigateProfile(n.target.username);
      else if (n.target.kind === 'group') handleNavigateGroup(n.target.slug);
      // target.kind === 'friend_request' intentionally has no case here -
      // that type gets dedicated Accept/Decline buttons instead of a plain
      // click-to-navigate (see handleRespondToFriendRequest + the notif
      // dropdown's rendering below).
  };

  const handleRespondToFriendRequest = async (n, action) => {
      try {
          await fetch('/api/users/friend-request/respond', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ approver: user.name, requester: n.target.username, action, notification_id: n.id })
          });
          setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch (e) { console.error("Friend request response failed", e); }
  };

  const handleAddPost = async (newPost) => { try { const res = await fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newPost) }); if (res.ok) { const created = await res.json(); setPosts(prev => [created, ...prev]); return created; } } catch (error) { console.error(error); } return null; };
  const handleDeletePost = async (id) => { try { const res = await fetch(`${API_URL}/${id}`, { method: 'DELETE' }); applyPostDelete(id); return res.ok; } catch (error) { console.error(error); } return false; };
  const handleLikeToggle = async (postId, voteType) => { try { const res = await fetch(`${API_URL}/${postId}/like`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: user.name, vote: voteType }) }); if (res.ok) { const updated = await res.json(); applyPostUpdate(updated); return updated; } } catch (error) { console.error(error); } return null; };
  const handleAddComment = async (postId, text, image) => { try { const res = await fetch(`${API_URL}/${postId}/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user: user.name, text, image }) }); if (res.ok) { const updated = await res.json(); applyPostUpdate(updated); return updated; } } catch (error) { console.error(error); } return null; };

  const handleVoteComment = async (postId, commentId, vote) => { if (!user) return null; try { const res = await fetch(`${API_URL}/${postId}/comment/${commentId}/vote`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: user.name, vote }) }); if (res.ok) { const updated = await res.json(); applyPostUpdate(updated); return updated; } } catch (error) { console.error(error); } return null; };

  const handleDeleteComment = async (postId, commentId) => {
      if (!user) return null;

      try {
          const res = await fetch(`${API_URL}/${postId}/comment/${commentId}`, {
              method: 'DELETE',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  username: user.name
              })
          });

          if (res.ok) { const data = await res.json(); applyPostUpdate(data.post); return data.post; }
      } catch (error) {
          console.error(error);
      }
      return null;
  };

  const handleEditComment = async (
      postId,
      commentId,
      text
  ) => {
      if (!user) return null;

      try {
          const res = await fetch(`${API_URL}/${postId}/comment/${commentId}`, {
              method: 'PUT',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  username: user.name,
                  text
              })
          });

          if (res.ok) { const data = await res.json(); applyPostUpdate(data.post); return data.post; }
      } catch (error) {
        console.error(error);
      }
      return null;
  };
  const handleEditPost = async (postId, newText, newVisibility) => {
      try {
          const res = await fetch(`${API_URL}/${postId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: newText, visibility: newVisibility })
          });
          if (res.ok) { const updated = await res.json(); applyPostUpdate(updated); return updated; }
      } catch (error) { console.error(error); }
      return null;
  };

  const triggerLogin = () => setShowLoginModal(true);

  const renderDynamicNav = () => {
    let activeLayout = user ? (user.menu_layout || MENU_ITEMS.map(i => i.id)) : ['home', 'groups', 'search'];

    if (user) {
        if (!activeLayout.includes('profile')) activeLayout = ['profile', ...activeLayout];
        if (!activeLayout.includes('home')) activeLayout = [...activeLayout, 'home'];
    }

    return activeLayout.map(id => {
        const item = MENU_ITEMS.find(i => i.id === id);
        if(!item) return null;
        let onClick = () => {};
        let content = <span style={{ fontSize: '24px' }}>{item.icon}</span>;
        let badge = null;

        switch(id) {
            case 'profile':
                onClick = () => user ? handleNavigateProfile(user.name) : triggerLogin();
                content = user && user.profile_pic ? 
                    <img src={getSafeImage(user.profile_pic)} style={{ width: '35px', height: '35px', borderRadius: '50%', objectFit: 'cover' }} alt="Profile" /> : 
                    <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: activeUser.avatarColor }}></div>;
                break;
            case 'home':
                onClick = handleGoHome;
                content = <img src="/newsfeed.png" alt="Home" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />;
                break;
            case 'messages':
                onClick = () => { 
                    if(!user) triggerLogin();
                    else { setView('messages'); setMessagesTab('chats'); }
                };
                if(user && totalUnread > 0) badge = <div style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#ff4757', color: 'white', borderRadius: '50%', minWidth: '18px', height: '18px', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', border: '2px solid #2f3542', padding: '0 4px' }}>{totalUnread > 99 ? '99+' : totalUnread}</div>;
                break;
            case 'groups':
                onClick = () => setView('groups');
                break;
            case 'nexus':
                onClick = () => { 
                    if(!user) triggerLogin();
                    else { setMessagesTab('ai'); setView('messages'); }
                };
                break;
            case 'search':
                onClick = () => setShowSearchModal(true);
                break;
            default: return null;
        }

        return (
            <div key={id} className="bottom-nav-icon" onClick={onClick} style={{ position: 'relative' }}>
                {content}
                {badge}
            </div>
        );
    });
  };

  if (window.location.pathname === '/reset-password') return <ResetPassword onComplete={() => window.location.href = '/'} backgroundStyle={backgroundStyle} />;

  if (window.location.pathname === '/notifications') return <NotificationPrefs onComplete={() => window.location.href = '/'} backgroundStyle={backgroundStyle} />;

  if (window.location.hostname.startsWith('admin-social.')) {
      return <AdminDashboard adminUser={user ? user.name : null} />;
  }

  // searchTag is only ever set by Widgets.js's trending-tag click
  // (tag.replace('#','')), so it's always a bare lowercase word with no
  // '#'. Previously matched with a loose .includes() against the whole
  // message body - reported bug: clicking "#ai" also matched any post
  // containing "ai" as a plain substring anywhere in the text (e.g.
  // "container", "maintain", "email"), not just posts actually tagged
  // #ai. Now extracts the real hashtags from the message (same /#\w+/g
  // pattern Widgets.js uses to build the trending list in the first
  // place) and checks for an exact match.
  const homePosts = searchTag
      ? (tagSearchResults || [])
      : posts.filter(p => p.visibility !== 'group');
  const activeUser = user || GUEST_USER_OBJ; 
  const unreadNotifs = notifications.filter(n => !n.read).length;

  return (
    <>
      {outageAlert && (
          <div style={{ width: '100%', background: '#dc2626', color: 'white', padding: '8px 16px', textAlign: 'center', fontWeight: 700, fontSize: '14px', position: 'relative', zIndex: 1000, boxSizing: 'border-box' }}>
              {outageAlert.message || 'We are currently experiencing an issue.'}
          </div>
      )}
    <div className="app-container">
      <PageTitle
        title={view === 'home' ? 'Nubix | Newsfeed' : 
                view === 'profile' ? `Nubix | ${viewedUser ? viewedUser.name : (user ? user.name : 'Profile')}` :
                'Nubix Social Network'} 
      />

      <div className="background-layer" style={backgroundStyle}></div>

      {showAppPrompt && (
          <div className="app-open-banner">
              <img src="/nubix.png" alt="Nubix" className="app-open-banner-icon" />
              <div className="app-open-banner-text">
                  <strong>Nubix Social Network</strong>
                  <span>Open in the app for the best experience</span>
              </div>
              <button className="app-open-banner-open" onClick={handleOpenAppFromPrompt}>Open</button>
              <button className="app-open-banner-dismiss" onClick={dismissAppPrompt} aria-label="Dismiss">✕</button>
          </div>
      )}

      <Sidebar
          onNavigate={handleSidebarNavigate} 
          onRefresh={handleGoHome} 
          onLogout={handleLogout} 
          user={activeUser} 
          messageCount={totalUnread}
          onSearch={setShowSearchModal}
      />
      
      <div className={`bottom-nav ${showBottomNav ? '' : 'hidden'}`} onTouchStart={handleMenuTouchStart} onTouchMove={handleMenuTouchMove}>
          {renderDynamicNav()}
      </div>
      
      <div className="main-content" ref={mainContentRef}>
        <div className="content-grid">
            <div className="feed-wrapper" ref={feedWrapperRef}>
                {view === 'home' && (
                    <>
                        <div className="card sticky-header-bar" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 20px', minHeight: '50px' }}>
                            <h2 onClick={handleHardRefresh} style={{ margin:0, fontSize:'1.2rem', cursor: 'pointer' }}>
                                {searchTag ? (<span>Trending: <span style={{color:'#ff4757'}}>{searchTag}</span></span>) : 'Newsfeed'}
                            </h2>
                            <div style={{ display:'flex', gap:'15px', alignItems:'center' }}>
                                {user && (
                                    <div 
                                        onClick={() => setShowNotifModal(!showNotifModal)} 
                                        style={{ cursor: 'pointer', fontSize: '1.2rem', position: 'relative', marginRight: '5px' }}
                                    >
                                        🔔
                                        {unreadNotifs > 0 && <div style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#ff4757', color: 'white', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>{unreadNotifs}</div>}
                                        
                                        {showNotifModal && (
                                            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '45px', right: '-10px', width: '340px', background: '#2f3542', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', zIndex: 10000, boxShadow: '0 10px 40px rgba(0,0,0,0.5)', padding: '15px', cursor: 'default' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                    <h4 style={{ margin: 0 }}>Notifications</h4>
                                                    <button onClick={handleClearNotifications} style={{ background: 'none', border: 'none', color: '#ff4757', cursor: 'pointer', fontSize: '0.8rem' }}>Clear All</button>
                                                </div>
                                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                    {notifications.length === 0 && <div style={{ color: '#aaa', fontSize: '0.9rem', textAlign: 'center', padding: '20px' }}>No new notifications</div>}
                                                    {notifications.map(n => n.type === 'friend_request' && !n.read ? (
                                                        <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.9rem' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff4757', flexShrink: 0 }}></span>
                                                                <span>{n.text}</span>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <button onClick={() => handleRespondToFriendRequest(n, 'accept')} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>Accept</button>
                                                                <button onClick={() => handleRespondToFriendRequest(n, 'decline')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem' }}>Decline</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div key={n.id} onClick={() => handleNotificationClick(n)} style={{ padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.9rem', color: n.read ? '#aaa' : 'white', cursor: n.target ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '8px' }}>{!n.read && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff4757', flexShrink: 0 }}></span>}<span>{n.text}</span></div>
                                                    ))}
                                                </div>
                                                <button onClick={() => setShowNotifModal(false)} style={{ width: '100%', marginTop: '10px', padding: '5px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer' }}>Close</button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {searchTag && (<button onClick={() => setSearchTag(null)} className="action-btn">Clear</button>)}
                                <div onClick={() => setShowTimeline(prev => !prev)} style={{ cursor: 'pointer', fontSize: '1.2rem', padding: '5px' }} title="Timeline">📅</div>
                                <div onClick={() => setShowSearchModal(true)} style={{ cursor: 'pointer', fontSize: '1.2rem', padding: '5px' }}>🔍</div>
                            </div>
                        </div>

                        <Feed
                            user={user}
                            posts={homePosts}
                            onTriggerLogin={triggerLogin}
                            showTimeline={showTimeline}
                            onCloseTimeline={() => setShowTimeline(false)}
                            hasMore={!searchTag && hasMorePosts}
                            onLoadMore={loadMorePosts}
                            initialText={introPromptText}
                            onInitialTextConsumed={() => setIntroPromptText(null)}
                            loadingMore={loadingMorePosts}
                            onAddPost={handleAddPost} onDeletePost={handleDeletePost} onLikePost={handleLikeToggle} onAddComment={handleAddComment} onVoteComment={handleVoteComment} onDeleteComment={handleDeleteComment} onEditComment={handleEditComment} onEdit={handleEditPost} onNavigateProfile={handleNavigateProfile}
                        />

                        <div className="mobile-footer" style={{ padding: '30px 20px', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                                <span onClick={() => handleLegalNavigate('terms')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Terms of Service</span>
                                <span onClick={() => handleLegalNavigate('privacy')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>
                            </div>
                            <div>Nubix Social Network • Nubix AI • © 2026</div>
                        </div>
                    </>
                )}
                {view === 'groups' && <Groups user={user} onNavigateGroup={handleNavigateGroup} onTriggerLogin={triggerLogin} />}
                {view === 'group_details' && <GroupDetails groupSlug={currentGroupSlug} user={user} onTriggerLogin={triggerLogin} onNavigateProfile={handleNavigateProfile} onBack={() => setView('groups')} onAddPost={handleAddPost} onDeletePost={handleDeletePost} onLikePost={handleLikeToggle} onAddComment={handleAddComment} onVoteComment={handleVoteComment} onDeleteComment={handleDeleteComment} onEditComment={handleEditComment} onEditPost={handleEditPost} />}
                {view === 'profile' && <Profile user={user} targetUser={viewedUser} onTriggerLogin={triggerLogin} onNavigate={setView} onNavigateProfile={handleNavigateProfile} onLogout={handleLogout} onUpdateUser={handleLogin} onDeletePost={handleDeletePost} onLikePost={handleLikeToggle} onAddComment={handleAddComment} onVoteComment={handleVoteComment} onDeleteComment={handleDeleteComment} onEditComment={handleEditComment} onEditPost={handleEditPost} initialParams={profileParams} onStartAIChat={handleStartAIChat} onStartMessage={handleStartMessage} onNavigatePost={handleNavigatePost} onSearchTag={handleSearchTag} />}
                {view === 'messages' && user && <Messages user={user} initialTab={messagesTab} initialMessage={feedbackPrompt} initialTarget={chatTarget} onClearInitial={() => setFeedbackPrompt(null)} onClearTarget={() => setChatTarget(null)} />}

                {view === 'terms' && <TermsOfService onBack={() => setView('home')} />}
                {view === 'privacy' && <PrivacyPolicy onBack={() => setView('home')} />}
                
                {view === 'post_details' && (
                    <div style={{ paddingBottom: '50px' }}>
                        <div className="card" style={{ marginBottom: '15px' }}>
                            <button onClick={handleGoHome} style={{ background: 'none', border: 'none', color: '#1e90ff', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                ⬅️ Back to Newsfeed
                            </button>
                        </div>
                        <Feed
                            user={user}
                            posts={viewedPost ? [viewedPost] : []}
                            onTriggerLogin={triggerLogin}
                            onAddPost={handleAddPost} onDeletePost={handleDeletePost} onLikePost={handleLikeToggle} onAddComment={handleAddComment} onVoteComment={handleVoteComment} onDeleteComment={handleDeleteComment} onEditComment={handleEditComment} onEdit={handleEditPost} onNavigateProfile={handleNavigateProfile}
                        />
                    </div>
                )}
            </div>
            
            <div className="widgets-area">
                {user && (
                    <Widgets
                        user={user}
                        onNavigatePost={handleNavigatePost}
                        onSearchTag={handleSearchTag}
                        onNavigateGallery={handleNavigateGallery}
                        onNavigate={handleLegalNavigate}
                    />
                )}
            </div>
        </div>
      </div>
      
      {showScrollTopBtn && view !== 'messages' && (
          <button
              className="scroll-top-btn"
              style={{ left: `${scrollBtnLeft}px` }}
              onClick={handleScrollToTopClick}
              title="Back to top"
              aria-label="Scroll to top"
          >
              ⬆️
          </button>
      )}

      <DraggableFeedback onClick={handleFeedbackClick} unreadCount={aiUnread} />
      
      {showSearchModal && (
          <div onClick={() => setShowSearchModal(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)', zIndex: 10000, display: 'flex', justifyContent: 'center', paddingTop: '80px' }}>
              <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '90%', maxWidth: '500px', height: 'fit-content', maxHeight: '70vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                      <span style={{ fontSize: '1.2rem' }}>🔍</span>
                      <input autoFocus placeholder="Search..." style={{ flex: 1, background: 'transparent', border: 'none', color: 'white', fontSize: '1.1rem', outline: 'none' }} value={globalSearchTerm} onChange={(e) => setGlobalSearchTerm(e.target.value)} />
                      <button onClick={() => setShowSearchModal(false)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer' }}>✕</button>
                  </div>
                  <div style={{ overflowY: 'auto' }}>
                      {isSearching && <div style={{ color: '#2ed573', textAlign: 'center', padding: '20px' }}>⏳ Searching...</div>}
                      {!isSearching && (
                        <>
                            {globalSearchResults.users.length > 0 && (<div style={{ marginBottom: '15px' }}><div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '5px', textTransform: 'uppercase' }}>People</div>{globalSearchResults.users.map(u => (<div key={u.name} onClick={() => handleNavigateProfile(u.name)} style={{ padding: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', marginBottom: '5px' }}><div style={{ width: '30px', height: '30px', borderRadius: '50%', background: u.avatarColor || '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{u.name[0]}</div><div><div>{u.name}</div><div style={{ fontSize: '0.8rem', color: '#aaa' }}>{u.handle}</div></div></div>))}{globalSearchHasMore.users && (<button onClick={() => handleLoadMoreSearch('users')} disabled={loadingMoreCategory === 'users'} style={{ width: '100%', background: 'transparent', border: 'none', color: '#1e90ff', fontWeight: 'bold', padding: '8px', cursor: 'pointer' }}>{loadingMoreCategory === 'users' ? '...' : 'Load More'}</button>)}</div>)}
                            {globalSearchResults.groups.length > 0 && (<div style={{ marginBottom: '15px' }}><div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '5px', textTransform: 'uppercase' }}>Communities</div>{globalSearchResults.groups.map(g => (<div key={g.id} onClick={() => { handleNavigateGroup(g.slug); setShowSearchModal(false); }} style={{ padding: '10px', cursor: 'pointer', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', marginBottom: '5px' }}>👥 {g.name}</div>))}{globalSearchHasMore.groups && (<button onClick={() => handleLoadMoreSearch('groups')} disabled={loadingMoreCategory === 'groups'} style={{ width: '100%', background: 'transparent', border: 'none', color: '#1e90ff', fontWeight: 'bold', padding: '8px', cursor: 'pointer' }}>{loadingMoreCategory === 'groups' ? '...' : 'Load More'}</button>)}</div>)}
                            {globalSearchResults.posts.length > 0 && (<div><div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '5px', textTransform: 'uppercase' }}>Posts</div>{globalSearchResults.posts.map(p => (<div key={p.id} onClick={() => handleNavigatePost(p.id)} style={{ padding: '10px', cursor: 'pointer', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', marginBottom: '5px' }}><div style={{ fontSize: '0.9rem' }}>{p.message.substring(0, 50)}...</div><div style={{ fontSize: '0.75rem', color: '#aaa' }}>by {p.username}</div></div>))}{globalSearchHasMore.posts && (<button onClick={() => handleLoadMoreSearch('posts')} disabled={loadingMoreCategory === 'posts'} style={{ width: '100%', background: 'transparent', border: 'none', color: '#1e90ff', fontWeight: 'bold', padding: '8px', cursor: 'pointer' }}>{loadingMoreCategory === 'posts' ? '...' : 'Load More'}</button>)}</div>)}
                            {globalSearchTerm && !isSearching && !globalSearchResults.users.length && !globalSearchResults.posts.length && !globalSearchResults.groups.length && (<div style={{ textAlign: 'center', color: '#777', padding: '20px' }}>No results found.</div>)}
                        </>
                      )}
                  </div>
              </div>
          </div>
      )}

      {showLoginModal && (
          <Login onLogin={handleLogin} onClose={() => setShowLoginModal(false)} backgroundStyle={{}} />
      )}
      
      {showWelcomeModal && welcomeConfig && (
          <div onClick={handleCloseWelcome} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 10001, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '90%', maxWidth: '500px', padding: '30px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)', position: 'relative' }}>
                  <h1 style={{ marginTop: 0, fontSize: '2.5rem', marginBottom: '10px' }}>👋</h1>
                  <h2 style={{ margin: '0 0 15px 0', fontSize: '1.8rem' }}>{welcomeConfig.welcome_title}</h2>
                  <div className="welcome-modal-msg" dangerouslySetInnerHTML={{ __html: welcomeConfig.welcome_message_html }} />
                  <div className="welcome-modal-whats-new" dangerouslySetInnerHTML={{ __html: welcomeConfig.whats_new_html }} />
                  <button onClick={handleCloseWelcome} style={{ background: 'linear-gradient(45deg, #1e90ff, #2ed573)', border: 'none', padding: '12px 30px', borderRadius: '30px', color: 'white', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', marginBottom: '20px', width: '100%' }}>Get Started</button>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#888', fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={dontShowWelcomeAgain} onChange={(e) => setDontShowWelcomeAgain(e.target.checked)} style={{ accentColor: '#1e90ff', transform: 'scale(1.2)' }}/>
                      Don't show this message again
                  </label>
              </div>
          </div>
      )}

      {/* --- NEW CSS STYLE BLOCK (UPDATED) --- */}
      <style>{`
        /* HOTFIX: Force Dark Mode & Fix White Gaps & Restore Wallpaper */
        :root {
            color-scheme: dark; /* Forces iOS Keyboard to Dark Mode */
        }
        html, body { 
            background-color: #1e1e24 !important; 
            overscroll-behavior: none; /* Prevents "bounce" revealing white background */
        }
        #root {
            background-color: transparent !important; /* Allow wallpaper to show through */
        }
        .background-layer {
            z-index: 0 !important; /* Bring wallpaper in front of body background, but behind content */
        }

        .mobile-footer { display: none; }
        @media (max-width: 1250px) {
            .mobile-footer { display: block; }
        }
      `}</style>

    </div>
    </>
  );
}

export default App;
